import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiTrash } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import activeLoadStatusIcon from "../../assets/images/Active_load.svg";
import inactiveStatusIcon from "../../assets/images/Inactive_icon.svg";
import prospectStatusIcon from "../../assets/images/Prospect_icon.svg";
import transporterIcon from "../../assets/images/transporterIcon.svg";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import NewTableComponent from "../../components/NewTableComponent";
import Stats from "../../components/Stats";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import { fetchTransporterCitiesDropdown } from "../../store/Slices/dropdownSlice";
import {
  deleteTransporter,
  fetchTransporterOverview,
  fetchTransportersPaginated,
} from "../../store/Slices/TransporterSlice";
import "../../styles/Table.css";
import "../../styles/TransportersTable.css";
import "../../styles/Trip.css";
import { dateMonthYear } from "../../utils/helper";
import { activeStatsIcon, lateStatsIcon, prospectIcon } from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";

const STATUS_FILTER_OPTIONS = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 2 },
  { label: "Prospect", value: 3 },
];

const STATUS_ID_TO_KEY = {
  1: "active",
  2: "inactive",
  3: "prospect",
};

const toText = (value) => String(value ?? "").trim();
const firstText = (...values) => values.map(toText).find(Boolean) || "";
const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalize = (value) => String(value || "").trim().toLowerCase();
const titleCase = (value) => {
  const text = String(value || "").replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "-";
};
const percent = (part, total) =>
  total ? `${((part / total) * 100).toFixed(1)}%` : "0%";
const cityOptions = [
  { label: "Islamabad", value: "islamabad" },
  { label: "Rawalpindi", value: "rawalpindi" },
  { label: "Lahore", value: "lahore" },
  { label: "Karachi", value: "karachi" },
  { label: "Faisalabad", value: "faisalabad" },
  { label: "Multan", value: "multan" },
  { label: "Peshawar", value: "peshawar" },
  { label: "Sialkot", value: "sialkot" },
];
const toPositiveNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getOptionLabel = (item) =>
  toText(item?.label ?? item?.name ?? item?.title ?? item?.city ?? item?.value);

const resolveStatus = (status) => {
  const statusNumber = Number(status);
  if (Number.isFinite(statusNumber) && STATUS_ID_TO_KEY[statusNumber]) {
    return STATUS_ID_TO_KEY[statusNumber];
  }

  return normalize(status);
};

const resolveVehicleTypeValue = (vehicleType) =>
  toText(
    vehicleType?.title ??
      vehicleType?.name ??
      vehicleType?.label ??
      vehicleType?.slug ??
      vehicleType
  );

const resolveServiceValue = (service) =>
  normalize(service?.service_title ?? service?.title ?? service);

const transporterStatusMeta = (status) => {
  const normalized = normalize(status);

  if (normalized === "active") {
    return {
      color: "#299900",
      icon: (
        <img
          src={activeLoadStatusIcon}
          alt="Active status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Active",
    };
  }

  if (normalized === "inactive") {
    return {
      color: "#EF0000",
      icon: (
        <img
          src={inactiveStatusIcon}
          alt="Inactive status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Inactive",
    };
  }

  if (normalized === "prospect") {
    return {
      color: "#F2841B",
      icon: (
        <img
          src={prospectStatusIcon}
          alt="Prospect status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Prospect",
    };
  }

  return {
    color: "#8F95B2",
    icon: null,
    label: titleCase(status),
  };
};

const buildTransporterSearchFilters = (filters = {}) => {
  const mapped = {};

  Object.entries(filters || {}).forEach(([filterName, filterObj]) => {
    const rawValue = filterObj?.value;
    const hasNoValue =
      rawValue === undefined ||
      rawValue === null ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0);

    if (!filterObj?.filter_type || hasNoValue) return;

    let filterType = filterObj.filter_type;
    let filterValue = rawValue;

    if (filterType === "equal_to") filterType = "equals";

    if (
      (filterName === "headquarterFilter" || filterName === "branchInCityFilter") &&
      filterType === "one_of"
    ) {
      const values = Array.isArray(filterValue) ? filterValue : [filterValue];
      if (values.length === 1) {
        filterType = "equals";
        filterValue = values[0];
      }
    }

    mapped[filterName] = {
      filter_type: filterType,
      value: filterValue,
    };
  });

  return mapped;
};

const Transporters = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    transporterOverviewData,
    transporterPaginatedData,
    transporterPaginatedLoading,
    deleteTransporterLoading,
  } = useSelector((state) => state.transporter);
  const { transporterCitiesDropdownData = [] } = useSelector(
    (state) => state.dropdowns
  );

  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const apiSearchFilters = useMemo(
    () => buildTransporterSearchFilters(searchFilters),
    [searchFilters]
  );

  useEffect(() => {
    dispatch(fetchTransporterOverview());
    dispatch(fetchTransporterCitiesDropdown());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    dispatch(
      fetchTransportersPaginated({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters: apiSearchFilters,
      })
    );
  }, [dispatch, currentPage, limit, searchText, apiSearchFilters]);

  const cityFilterOptions = useMemo(() => {
    const source = transporterCitiesDropdownData?.length
      ? transporterCitiesDropdownData
      : cityOptions;

    const uniqueMap = new Map();
    source.forEach((item) => {
      const label = getOptionLabel(item);
      if (!label) return;
      const key = normalize(label);
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, { label, value: label });
      }
    });

    return Array.from(uniqueMap.values());
  }, [transporterCitiesDropdownData]);

  const apiRows = useMemo(
    () =>
      safeArray(transporterPaginatedData?.results).map((item, index) => {
        const transportersPoc = safeArray(item?.transporters_poc);
        const primaryPoc =
          transportersPoc.find((poc) => Boolean(poc?.is_primary)) ||
          transportersPoc[0] ||
          {};

        const secondaryContacts = transportersPoc
          .filter((poc) => !poc?.is_primary)
          .map((poc, contactIndex) => ({
            id: poc?.id || `${item?.id || index}-secondary-${contactIndex}`,
            name: toText(poc?.name),
            contact: toText(poc?.contact),
            designation: toText(poc?.designation),
            email: toText(poc?.email),
          }));

        const branches = safeArray(item?.branches);
        const mappedBranches = branches.map((branch, branchIndex) => {
          const branchVehicleTypes = safeArray(branch?.vehicle_types)
            .map((vehicleType) => {
              const id = Number(vehicleType?.id ?? vehicleType?.vehicle_type_id);
              if (Number.isFinite(id) && id > 0) return id;
              return resolveVehicleTypeValue(vehicleType);
            })
            .filter(Boolean);

          return {
            id: branch?.id || `${item?.id || index}-branch-${branchIndex}`,
            branchName: toText(branch?.branch_name ?? branch?.name),
            city: toText(branch?.city),
            contact: toText(branch?.contact),
            fleetSize: branch?.fleet_size ?? "",
            contactPersonName: toText(branch?.contact_person_name),
            contactPersonNumber: toText(branch?.contact_person_number),
            branchAddress: toText(branch?.address ?? branch?.branch_address),
            branchLatitude: firstText(
              branch?.lat,
              branch?.latitude,
              branch?.branch_lat,
              branch?.branch_latitude
            ),
            branchLongitude: firstText(
              branch?.lng,
              branch?.longitude,
              branch?.branch_lng,
              branch?.branch_longitude
            ),
            vehicleTypesSupported: branchVehicleTypes,
            supportedServices: safeArray(branch?.supported_services)
              .map(resolveServiceValue)
              .filter(Boolean),
          };
        });

        const topLevelVehicleTypes = safeArray(item?.vehicle_types)
          .map((vehicleType) => {
            const id = Number(vehicleType?.id ?? vehicleType?.vehicle_type_id);
            if (Number.isFinite(id) && id > 0) return id;
            return resolveVehicleTypeValue(vehicleType);
          })
          .filter(Boolean);

        const vehicleTypesForTable = safeArray(item?.vehicle_types)
          .map(resolveVehicleTypeValue)
          .filter(Boolean);

        const servicesForForm = safeArray(item?.supported_services)
          .map(resolveServiceValue)
          .filter(Boolean);

        const files = safeArray(item?.files);
        const status = resolveStatus(item?.status) || "prospect";
        const recordId = item?.id ?? `${item?.transporter_id || "transporter"}-${index}`;
        const transporterId = toText(item?.transporter_id) || `TPT-${recordId}`;
        const branchCities = mappedBranches.map((branch) => branch.city).filter(Boolean);

        return {
          id: transporterId,
          recordId,
          name: toText(item?.name) || "-",
          headquarter: toText(item?.headquarter),
          contact: toText(item?.contact) || "-",
          primaryContactPerson: toText(primaryPoc?.name) || "-",
          fleetSize: Number(item?.fleet_size || 0),
          status,
          branchesCities: branchCities,
          vehicleTypesSupported: vehicleTypesForTable,
          supportedServices: servicesForForm,
          attachmentsCount: files.length,
          updatedAt: item?.updated_at || item?.created_at || null,
          email: toText(item?.email),
          website: toText(item?.website),
          address: toText(item?.address),
          addressLatitude: firstText(
            item?.lat,
            item?.latitude,
            item?.address_lat,
            item?.address_latitude
          ),
          addressLongitude: firstText(
            item?.lng,
            item?.longitude,
            item?.address_lng,
            item?.address_longitude
          ),
          formData: {
            transporterId,
            transporterName: toText(item?.name),
            contact: toText(item?.contact),
            email: toText(item?.email),
            website: toText(item?.website),
            headquarter: toText(item?.headquarter),
            address: toText(item?.address),
            addressLatitude: firstText(
              item?.lat,
              item?.latitude,
              item?.address_lat,
              item?.address_latitude
            ),
            addressLongitude: firstText(
              item?.lng,
              item?.longitude,
              item?.address_lng,
              item?.address_longitude
            ),
            fleetSize: item?.fleet_size ?? "",
            status,
            vehicleTypesSupported: topLevelVehicleTypes,
            supportedServices: servicesForForm,
            primaryContact: {
              name: toText(primaryPoc?.name),
              contact: toText(primaryPoc?.contact),
              designation: toText(primaryPoc?.designation),
              email: toText(primaryPoc?.email),
            },
            secondaryContacts,
            noBranches: !(item?.has_branches && mappedBranches.length > 0),
            branches: mappedBranches,
            attachments: files,
            payment: {
              billingFrequency: toText(item?.billing_frequency),
              paymentMethod: toText(item?.payment_method),
              paymentTerm: toText(item?.payment_terms),
              bank: toText(item?.bank_name),
              accountTitle: toText(item?.account_title),
              iban: toText(item?.iban_number),
            },
            notes: toText(item?.note),
          },
        };
      }),
    [transporterPaginatedData]
  );

  const rows = useMemo(() => apiRows, [apiRows]);

  const selectedDeleteRow = useMemo(
    () => rows.find((item) => item.recordId === deleteId) || null,
    [rows, deleteId]
  );
  const selectedDeleteName = selectedDeleteRow?.name?.trim() || "this record";
  const deleteTitle = "Are you sure you want to delete this transporter?";
  const deleteDescription = `Deleting "${selectedDeleteName}" will permanently remove this transporter from your records. This action cannot be undone.`;

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteId(null);
  };

  const totalPages = toPositiveNumber(transporterPaginatedData?.meta?.totalPages, 1);
  const totalItems = (() => {
    const metaCount = Number(transporterPaginatedData?.meta?.totalItems);
    if (Number.isFinite(metaCount) && metaCount >= 0) return metaCount;
    const total = Number(transporterPaginatedData?.total);
    if (Number.isFinite(total) && total >= 0) return total;
    return rows.length;
  })();

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [currentPage, totalPages]);

  const localStats = useMemo(() => {
    const totalFallback = totalItems || rows.length;
    const active = rows.filter((item) => item.status === "active").length;
    const inactive = rows.filter((item) => item.status === "inactive").length;
    const prospect = rows.filter((item) => item.status === "prospect").length;
    const last = rows
      .map((item) => item.updatedAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return { total: totalFallback, active, inactive, prospect, last };
  }, [rows, totalItems]);

  const getMetric = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const totalCount = getMetric(
    transporterOverviewData?.total_transporter,
    localStats.total
  );
  const activeCount = getMetric(
    transporterOverviewData?.active?.count,
    localStats.active
  );
  const inactiveCount = getMetric(
    transporterOverviewData?.inactive?.count,
    localStats.inactive
  );
  const prospectCount = getMetric(
    transporterOverviewData?.prospect?.count,
    localStats.prospect
  );

  const getPercentage = (value, fallbackPart, fallbackTotal) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return `${parsed.toFixed(1)}%`;
    return percent(fallbackPart, fallbackTotal);
  };

  const activePercentage = getPercentage(
    transporterOverviewData?.active?.percentage,
    activeCount,
    totalCount
  );
  const inactivePercentage = getPercentage(
    transporterOverviewData?.inactive?.percentage,
    inactiveCount,
    totalCount
  );
  const prospectPercentage = getPercentage(
    transporterOverviewData?.prospect?.percentage,
    prospectCount,
    totalCount
  );
  const lastUpdatedAt = transporterOverviewData?.last_updated || localStats.last;

  const statsData = [
    {
      title: "Total Transporter",
      value: totalCount,
      text: `Last updated : ${lastUpdatedAt ? dateMonthYear(lastUpdatedAt) : "--"}`,
      icon: (
        <img
          src={transporterIcon}
          alt="Total transporter icon"
          width={46}
          height={46}
        />
      ),
    },
    {
      title: "Active",
      value: activeCount,
      text: (
        <span>
          <b>{activePercentage}</b> from total
        </span>
      ),
      icon: activeStatsIcon,
      className: "completed",
    },
    {
      title: "Inactive",
      value: inactiveCount,
      text: (
        <span>
          <b>{inactivePercentage}</b> from total
        </span>
      ),
      icon: lateStatsIcon,
      className: "late",
    },
    {
      title: "Prospect",
      value: prospectCount,
      text: (
        <span>
          <b>{prospectPercentage}</b> from total
        </span>
      ),
      icon: prospectIcon,
      className: "pending-delivery",
    },
  ];

  const columns = [
    {
      name: "Transporter ID",
      key: "id",
      tableColumn: true,
      filterName: "idFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "125px",
      selector: (row) => (
        <button
          type="button"
          className="hyperlink"
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            color: "#1447b1",
            textDecoration: "underline",
            fontWeight: 600,
          }}
          onClick={() => navigate(`/transporters/details/${row.recordId}`)}
        >
          {row.id}
        </button>
      ),
    },
    {
      name: "Name",
      key: "name",
      selector: (row) => (
        <span className="hyperlink" style={{ textTransform: "capitalize" }}>
          {row.name}
        </span>
      ),
      tableColumn: true,
      filterName: "nameFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "145px",
    },
    {
      name: "Headquarter",
      key: "headquarter",
      selector: (row) => titleCase(row.headquarter),
      tableColumn: true,
      filterName: "headquarterFilter",
      type: "select",
      options: cityFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "130px",
    },
    {
      name: "Contact Number",
      key: "contact",
      selector: (row) => row.contact || "-",
      tableColumn: true,
      filterName: "contactFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "140px",
    },
    {
      name: "Primary Contact Person",
      key: "primaryContactPerson",
      selector: (row) => (
        <span className="hyperlink" style={{ textTransform: "capitalize" }}>
          {row.primaryContactPerson || "-"}
        </span>
      ),
      tableColumn: true,
      filterName: "primaryContactFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "220px",
      width: "220px",
    },
    {
      name: "Fleet Size",
      key: "fleetSize",
      selector: (row) => row.fleetSize || "-",
      tableColumn: true,
      filterName: "fleetSizeFilter",
      type: "number",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "100px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => {
        const meta = transporterStatusMeta(row.status);

        return (
          <span className="transporter_status_cell" style={{ color: meta.color }}>
            {meta.icon}
            {meta.label}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "select",
      options: STATUS_FILTER_OPTIONS,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "110px",
    },
    {
      name: "Vehicle Types Supported",
      key: "vehicleTypesSupported",
      selector: (row) => {
        const types = safeArray(row.vehicleTypesSupported);
        if (!types.length) return "-";

        const firstTwo = types.slice(0, 2);
        const remaining = types.slice(2);

        return (
          <div className="transporter_vehicle_types_cell">
            {firstTwo.map((item) => (
              <span
                key={`${row.recordId}-${item}`}
                className="transporter_vehicle_type_chip"
              >
                {titleCase(item)}
              </span>
            ))}
            {remaining.length > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {remaining.map((item) => (
                      <div
                        key={`${row.recordId}-remaining-${item}`}
                        className="tooltip_item"
                      >
                        {titleCase(item)}
                      </div>
                    ))}
                  </Tooltip>
                }
              >
                <span
                  className="transporter_vehicle_type_more"
                >
                  +{remaining.length}
                </span>
              </OverlayTrigger>
            )}
          </div>
        );
      },
      tableColumn: false,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "280px",
      width: "280px",
    },
    {
      name: "Action",
      key: "actions",
      selector: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="delete_btn"
            title="Delete"
            onClick={() => {
              setDeleteId(row.recordId);
              setDeletePopup(true);
            }}
          >
            <PiTrash />
          </button>
        </div>
      ),
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: false,
      sortable: false,
      tableColumn: false,
      minWidth: "110px",
      grow: 0,
    },
  ];

  const handleDelete = async () => {
    if (deleteId === null || deleteId === undefined || deleteTransporterLoading) return;

    const targetId = Number(deleteId);
    if (!Number.isFinite(targetId)) {
      errorNotify("Invalid transporter id");
      return;
    }

    try {
      await dispatch(deleteTransporter(targetId)).unwrap();
      successNotify("Transporter deleted successfully");
      closeDeletePopup();
      dispatch(fetchTransporterOverview());

      if (rows.length === 1 && currentPage > 1) {
        setCurrentPage((previous) => Math.max(1, previous - 1));
        return;
      }

      dispatch(
        fetchTransportersPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters: apiSearchFilters,
        })
      );
    } catch (error) {
      errorNotify(error || "Failed to delete transporter");
    }
  };

  const processedRows = rows.map((item, index, arr) => ({
    ...item,
    isLastFourServiceRow: index >= arr.length - 4,
  }));

  return (
    <div>
      <Breadcrumbs values={["Transporters"]} />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDelete}
        loading={deleteTransporterLoading}
        title={deleteTitle}
        description={deleteDescription}
      />

      <div className="access_view">
        <h2>Transporters</h2>

        <div className="services_buttons">
          <button onClick={() => navigate("/transporters/add")}>
            + Add Transporter
          </button>
        </div>
      </div>

      <Stats statsData={statsData} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={processedRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={transporterPaginatedLoading}
        paginationLoading={transporterPaginatedLoading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        tableClassName="transporters_table"
      />

      {!transporterPaginatedLoading && processedRows.length === 0 && (
        <div className="add_trip_suggestion_div" style={{ marginTop: 0 }}>
          <h4>There is nothing here...</h4>
          <p>
            There is nothing to view right now, please add new transporter data
            to get started.
          </p>
          <Button
            submitText="Add Transporter"
            type="button"
            shouldRow={false}
            isShowSubmitBtn
            isShowCancel={false}
            submitBtn={() => navigate("/transporters/add")}
          />
        </div>
      )}
    </div>
  );
};

export default Transporters;
